import { Head } from "@inertiajs/react";
import { useReveal } from "@/hooks/useReveal";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { Hero } from "@/components/ui/landing/Hero";
import { ProofSection } from "@/components/ui/landing/ProofSection";
import { MethodSection } from "@/components/ui/landing/MethodSection";
import { LanguagesGrid } from "@/components/ui/landing/LanguagesGrid";
import { PullQuote } from "@/components/ui/landing/PullQuote";
import { LANGUAGES, PULL_QUOTE } from "@/content/landing";

export default function Home() {
  const [proofRef, proofVisible] = useReveal<HTMLDivElement>();
  const [methodRef, methodVisible] = useReveal<HTMLDivElement>();
  const [languagesRef, languagesVisible] = useReveal<HTMLElement>();
  const [quoteRef, quoteVisible] = useReveal<HTMLDivElement>();

  return (
    <PublicLayout>
        <Head>
          <title>Mímir — read your way to a language</title>
          <meta
            name="description"
            content="Real texts, gently glossed. Tap any word for instant meaning, and let it settle into memory at its own pace. No streaks, no points — just you and the page."
          />
          <link rel="canonical" href="https://mimirlang.eu/" />
          <meta
            property="og:title"
            content="Mímir — read your way to a language"
          />
          <meta
            property="og:description"
            content="Real texts, gently glossed. Tap any word for instant meaning, and let it settle into memory at its own pace."
          />
          <meta property="og:url" content="https://mimirlang.eu/" />
          <meta property="og:site_name" content="Mímir Languages" />
          <meta property="og:type" content="website" />
          <meta property="og:locale" content="en_US" />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:title" content="Mímir — read your way to a language" />
          <meta
            name="twitter:description"
            content="Real texts, gently glossed. Tap any word for instant meaning."
          />
        </Head>

          <Hero />

          <div
            ref={proofRef}
            className={`reveal${proofVisible ? " visible" : ""}`}
          >
            <ProofSection />
          </div>

          <div
            ref={methodRef}
            className={`reveal${methodVisible ? " visible" : ""}`}
          >
            <MethodSection />
          </div>

          <section
            id="languages"
            ref={languagesRef}
            className={`reveal max-w-5xl mx-auto px-4 sm:px-8 py-16 sm:py-24${languagesVisible ? " visible" : ""}`}
          >
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
              <h2 className="font-display text-2xl sm:text-3xl text-heading">Languages</h2>
              <span className="font-mono text-xs uppercase tracking-widest text-muted">
                More arriving each season
              </span>
            </div>
            <LanguagesGrid languages={LANGUAGES} />
          </section>

          <div
            ref={quoteRef}
            className={`reveal${quoteVisible ? " visible" : ""}`}
          >
            <PullQuote text={PULL_QUOTE.text} author={PULL_QUOTE.author} />
          </div>
    </PublicLayout>
  );
}
